// src/components/SupplierSignup.js

import {
  CheckCircleOutlined,
  GiftOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  Space,
  Steps,
  Typography,
} from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AddressAutocomplete from "./AddressAutocomplete";

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

// South African phone number utility functions
const formatSAPhoneNumber = (value) => {
  if (!value) return value;

  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, "");

  // Check if it starts with 0
  if (phoneNumber.startsWith("0")) {
    // Format as 0XX XXX XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3)}`;
    } else {
      return `${phoneNumber.slice(0, 3)} ${phoneNumber.slice(
        3,
        6
      )} ${phoneNumber.slice(6, 10)}`;
    }
  }
  // Check if it starts with 27 (country code)
  else if (phoneNumber.startsWith("27")) {
    // Format as +27 XX XXX XXXX
    if (phoneNumber.length <= 2) {
      return `+${phoneNumber}`;
    } else if (phoneNumber.length <= 4) {
      return `+${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2)}`;
    } else if (phoneNumber.length <= 7) {
      return `+${phoneNumber.slice(0, 2)} ${phoneNumber.slice(
        2,
        4
      )} ${phoneNumber.slice(4)}`;
    } else {
      return `+${phoneNumber.slice(0, 2)} ${phoneNumber.slice(
        2,
        4
      )} ${phoneNumber.slice(4, 7)} ${phoneNumber.slice(7, 11)}`;
    }
  }
  // Check if it's entered without country code or leading 0
  else if (phoneNumber.length >= 9 && phoneNumber.length <= 10) {
    // Format as 0XX XXX XXXX (assuming it's a valid SA number without the leading 0)
    return `0${phoneNumber.slice(0, 2)} ${phoneNumber.slice(
      2,
      5
    )} ${phoneNumber.slice(5, 9)}`;
  }
  // For international format without +
  else if (phoneNumber.length > 0) {
    return `+${phoneNumber}`;
  }

  return phoneNumber;
};

const validateSAPhoneNumber = (_, value) => {
  if (!value) {
    return Promise.reject(new Error("Please enter your phone number"));
  }

  // Remove all non-digit characters for validation
  const phoneNumber = value.replace(/\D/g, "");

  // Check if it's a valid South African number
  // SA mobile numbers: 10 digits starting with 0, or 11 digits starting with 27
  // Common mobile prefixes: 060, 061, 062, 063, 064, 065, 066, 067, 068, 071, 072, 073, 074, 076, 078, 079, 081, 082, 083, 084

  const validPrefixes = [
    "060",
    "061",
    "062",
    "063",
    "064",
    "065",
    "066",
    "067",
    "068",
    "071",
    "072",
    "073",
    "074",
    "076",
    "078",
    "079",
    "081",
    "082",
    "083",
    "084",
  ];

  // Check for format with leading 0
  if (phoneNumber.startsWith("0") && phoneNumber.length === 10) {
    const prefix = phoneNumber.substring(0, 3);
    if (validPrefixes.includes(prefix)) {
      return Promise.resolve();
    }
  }

  // Check for format with country code 27
  if (phoneNumber.startsWith("27") && phoneNumber.length === 11) {
    const prefix = "0" + phoneNumber.substring(2, 4);
    if (validPrefixes.includes(prefix)) {
      return Promise.resolve();
    }
  }

  // Check for format with + country code
  if (phoneNumber.startsWith("27") && phoneNumber.length === 11) {
    const prefix = "0" + phoneNumber.substring(2, 4);
    if (validPrefixes.includes(prefix)) {
      return Promise.resolve();
    }
  }

  return Promise.reject(
    new Error("Please enter a valid South African phone number")
  );
};

const SupplierSignup = () => {
  const showsupplierintro = true;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [formValid, setFormValid] = useState(false);

  // Validate current step fields before proceeding
  const validateStepAndProceed = async (nextStep) => {
    try {
      // Get fields for the current step
      let fieldsToValidate = [];

      if (currentStep === 0) {
        fieldsToValidate = ["businessName", "contactPerson"];
      } else if (currentStep === 1) {
        fieldsToValidate = ["email", "phone", "promocode"];
      } else if (currentStep === 2) {
        fieldsToValidate = ["address", "password", "confirmPassword"];
      }

      // Validate only the fields in the current step
      await form.validateFields(fieldsToValidate);

      // If validation passes, proceed to next step
      setCurrentStep(nextStep);

      // If moving to step 2, immediately check validity for the submit button
      if (nextStep === 2) {
        setTimeout(() => {
          const values = form.getFieldsValue([
            "address",
            "password",
            "confirmPassword",
          ]);
          if (values.address && values.password && values.confirmPassword) {
            form
              .validateFields(["address", "password", "confirmPassword"])
              .then(() => setFormValid(true))
              .catch(() => setFormValid(false));
          }
        }, 100);
      }
    } catch (errorInfo) {
      // If validation fails, show error message
      console.log("Validation failed:", errorInfo);
      message.error("Please fix the errors before proceeding");
    }
  };

  // Add effect to check validity when step changes
  useEffect(() => {
    // Always validate the final step when it becomes active
    if (currentStep === 2) {
      const values = form.getFieldsValue([
        "address",
        "password",
        "confirmPassword",
      ]);
      // Check if we have any values to validate
      const hasValues =
        values.address || values.password || values.confirmPassword;

      if (hasValues) {
        form
          .validateFields(["address", "password", "confirmPassword"])
          .then(() => {
            // Only set valid if all required fields have values
            if (values.address && values.password && values.confirmPassword) {
              setFormValid(true);
            } else {
              setFormValid(false);
            }
          })
          .catch(() => {
            setFormValid(false);
          });
      } else {
        setFormValid(false);
      }
    }
  }, [currentStep, form]);

  // Handle form value changes to validate in real-time
  const onFormValuesChange = (changedValues, allValues) => {
    if (currentStep === 2) {
      const { address, password, confirmPassword } = allValues;

      // Only attempt validation if all required fields have values
      if (address && password && confirmPassword) {
        // Use a slight delay to ensure values are updated
        setTimeout(() => {
          form
            .validateFields(["address", "password", "confirmPassword"])
            .then(() => {
              setFormValid(true);
            })
            .catch(() => {
              setFormValid(false);
            });
        }, 0);
      } else {
        setFormValid(false);
      }
    }
  };

  const onSelectAddress = ({ address, lat, lng }) => {
    form.setFieldsValue({ address, lat, lng });
    // Trigger validation check after address update
    if (currentStep === 2) {
      validateStepAndProceed(2);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/marketplace/suppliers-signup`,
        values
      );
      message.success(response.data.message);
      window.location.href = "/supplier/login";
    } catch (error) {
      message.error(error.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: <CheckCircleOutlined />, text: "Reach a wider local audience" },
    { icon: <CheckCircleOutlined />, text: "Grow your business" },
    { icon: <CheckCircleOutlined />, text: "Get paid instantly" },
    { icon: <CheckCircleOutlined />, text: "Local support" },
    { icon: <CheckCircleOutlined />, text: "No listing fees" },
    { icon: <CheckCircleOutlined />, text: "0% commissions on sales" },
    { icon: <CheckCircleOutlined />, text: "Free to join" },
  ];

  // Handle phone number input change for formatting
  const onPhoneChange = (e) => {
    const { value } = e.target;
    const formattedValue = formatSAPhoneNumber(value);
    form.setFieldsValue({ phone: formattedValue });
  };

  return (
    <div
      className="supplier-signup-container"
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "10px 10px 20px 10px",
      }}
    >
      <Row gutter={[24, 24]}>
        <Col
          xs={24}
          md={24}
          lg={showsupplierintro ? 14 : 24}
          xl={showsupplierintro ? 16 : 24}
        >
          <div
            className="signup-form-container"
            style={{
              background: "#fff",
              borderRadius: "8px",
              padding: "10px 10px 50px 10px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Title
              level={1}
              style={{ marginBottom: "44px", textAlign: "center" }}
            >
              Join <span style={{ color: "#4f6367" }}>QikMarket</span> as a{" "}
              <span style={{ color: "orange" }}> Supplier</span> or{" "}
              <span style={{ color: "orange" }}> Seller</span>
            </Title>
            <Paragraph
              style={{
                marginBottom: "24px",
                fontSize: "16px",
                textAlign: "center",
                marginLeft: "10px",
                marginRight: "10px",
              }}
            >
              <Text>
                <strong>QikMarket</strong> is a free local marketplace that
                connects suppliers with local buyers. Join thousands of
                successful sellers and{" "}
                <Text strong>start growing your business today</Text> with our
                easy-to-use platform and powerful selling tools!
              </Text>
            </Paragraph>

            <div
              style={{
                padding: "10px 0 20px",
                borderBottom: "1px solid #eee",
                marginBottom: "20px",
              }}
            >
              <Steps
                current={currentStep}
                onChange={setCurrentStep}
                style={{
                  marginBottom: "0",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
              >
                <Step title="Business Info" icon={<ShopOutlined />} />
                <Step title="Contact Details" icon={<UserOutlined />} />
                <Step title="Location & Security" icon={<LockOutlined />} />
              </Steps>
            </div>

            <Form
              name="supplier_signup"
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={onFormValuesChange}
              autoComplete="off"
              className="login-form"
              requiredMark="optional"
              size="large"
            >
              <div style={{ display: currentStep === 0 ? "block" : "none" }}>
                <Form.Item
                  label="Store Name"
                  name="businessName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your business name",
                    },
                  ]}
                >
                  <Input
                    prefix={<ShopOutlined className="input-icon" />}
                    placeholder="Enter your business name"
                    className="custom-input"
                  />
                </Form.Item>

                <Form.Item
                  label="Contact Person"
                  name="contactPerson"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the contact person name",
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="input-icon" />}
                    placeholder="Enter contact person name"
                    className="custom-input"
                  />
                </Form.Item>

                <div style={{ textAlign: "right", marginTop: "20px" }}>
                  <Button
                    type="primary"
                    onClick={() => validateStepAndProceed(1)}
                  >
                    Next
                  </Button>
                </div>
              </div>

              <div style={{ display: currentStep === 1 ? "block" : "none" }}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined className="input-icon" />}
                    placeholder="Enter your email"
                    className="custom-input"
                  />
                </Form.Item>

                <Form.Item
                  label="Phone Number"
                  name="phone"
                  rules={[
                    { validator: validateSAPhoneNumber },
                    {
                      required: true,
                      message: "Please enter your phone number",
                    },
                  ]}
                  extra="Enter a South African phone number (e.g., 071 123 4567 or +27 71 123 4567)"
                >
                  <Input
                    prefix={<PhoneOutlined className="input-icon" />}
                    placeholder="Enter your phone number"
                    className="custom-input"
                    onChange={onPhoneChange}
                    maxLength={16}
                  />
                </Form.Item>

                <Form.Item label={<span>Referral code</span>} name="promocode">
                  <Input
                    prefix={<GiftOutlined className="input-icon" />}
                    placeholder="Enter a referral code"
                    className="custom-input"
                  />
                </Form.Item>

                <div
                  style={{
                    textAlign: "right",
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    style={{ flex: "1", marginRight: "10px" }}
                    onClick={() => setCurrentStep(0)}
                  >
                    Previous
                  </Button>
                  <Button
                    type="primary"
                    style={{ flex: "1" }}
                    onClick={() => validateStepAndProceed(2)}
                  >
                    Next
                  </Button>
                </div>
              </div>

              <div style={{ display: currentStep === 2 ? "block" : "none" }}>
                <Form.Item
                  label="Business Address"
                  name="address"
                  rules={[
                    { required: true, message: "Please enter your address" },
                  ]}
                >
                  <AddressAutocomplete
                    onSelectAddress={onSelectAddress}
                    className="custom-input"
                    placeholder="Enter your business address"
                    style={{
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "6px",
                      width: "100%",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters",
                    },
                  ]}
                  extra="Create a strong password with at least 8 characters"
                >
                  <Input.Password
                    prefix={<LockOutlined className="input-icon" />}
                    placeholder="Enter a secure password"
                    className="custom-input"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm Password"
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("The two passwords do not match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="input-icon" />}
                    placeholder="Confirm your password"
                    className="custom-input"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    disabled={!formValid}
                  >
                    Create Supplier Account
                  </Button>
                </Form.Item>

                <div
                  style={{
                    textAlign: "right",
                    marginTop: "10px",
                    display: "flex",
                  }}
                >
                  <Button
                    style={{ flex: "1" }}
                    onClick={() => setCurrentStep(1)}
                  >
                    Previous
                  </Button>
                </div>
              </div>
            </Form>

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <Space direction="vertical">
                <Text>Already have an account?</Text>
                <Space>
                  <Link to="/supplier/login">
                    <Button type="link">Login as a supplier</Button>
                  </Link>
                  <Divider type="vertical" />
                  <Link to="/buyer/login">
                    <Button type="link">Login as a buyer</Button>
                  </Link>
                  <Divider type="vertical" />
                  <Link to="/">
                    <Button type="link">Back to Home</Button>
                  </Link>
                </Space>
              </Space>
            </div>
          </div>
        </Col>

        {showsupplierintro && (
          <Col xs={24} md={24} lg={10} xl={8}>
            <div
              className="benefits-container"
              style={{
                background: "#fff",
                borderRadius: "8px",
                padding: "30px",
                height: "100%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Title
                level={3}
                style={{ marginBottom: "24px", color: "orange" }}
              >
                Sell More on QikMarket
              </Title>

              <Paragraph style={{ marginBottom: "24px" }}>
                <Text strong>QikMarket</Text> is a free local marketplace that
                connects you with buyers in your area.{" "}
                <Text strong>Start selling today</Text> and grow your business
                with us!
              </Paragraph>

              <Card
                style={{
                  marginBottom: "24px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "24px" }}
              >
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <div
                        style={{
                          color: "#52c41a",
                          marginRight: "12px",
                          fontSize: "18px",
                        }}
                      >
                        {benefit.icon}
                      </div>
                      <Text strong>{benefit.text}</Text>
                    </div>
                  ))}
                </Space>
              </Card>

              <div style={{ textAlign: "center", marginTop: "30px" }}>
                <img
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    maxWidth: "400px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  }}
                  src="/distribution-center.jpg"
                  alt="Distribution Center"
                />
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default SupplierSignup;
